import React, { useState, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { FaPlus, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { getToken } from "../../api/auth";
import {
  getUsersAllowance,
  addUserAllowance,
  getUserById,
  getAllUsers,
  getDocumentTypes,
} from "../../api/user";
import "./DocumentAllowance.css";

const toDocTypesMap = (data) => {
  const map = {};
  if (Array.isArray(data)) {
    data.forEach((item) => {
      const id = item?.documentType1;
      const name = item?.description;
      if (id != null) map[id] = name || String(id);
    });
  }
  return map;
};

const getUsernameFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.unique_name || payload.sub || null;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
};

const DocumentAllowances = () => {
  const token = getToken();
  const username = getUsernameFromToken();

  const [allowances, setAllowances] = useState([]);
  const [currentUserCompanyCode, setCurrentUserCompanyCode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageInput, setPageInput] = useState("1");
  const [sortConfig, setSortConfig] = useState({
    key: "username",
    direction: "asc",
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [formData, setFormData] = useState({
    companyCode: "",
    username: "",
    documentType: "",
    locationCode: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Document types from API
  const [docTypesMap, setDocTypesMap] = useState({});
  const [docTypesError, setDocTypesError] = useState(null);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!username) return;

      try {
        const userData = await getUserById(username);
        setCurrentUserCompanyCode(userData.companyCode);
        setFormData((prev) => ({ ...prev, companyCode: userData.companyCode }));
      } catch (err) {
        setError("Failed to load your user profile.");
        console.error(err);
      }
    };

    if (token && username) {
      fetchCurrentUser();
    }
  }, [token, username]);

  // Fetch document types once
  useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const types = await getDocumentTypes();
        setDocTypesMap(toDocTypesMap(types));
      } catch (err) {
        console.error("Failed to fetch document types:", err);
        setDocTypesError("Failed to load document types.");
      }
    };
    if (token) fetchDocTypes();
  }, [token]);

  // Fetch allowances and filter by current user's company
  useEffect(() => {
    const fetchAllowances = async () => {
      if (!currentUserCompanyCode) return;

      setLoading(true);
      try {
        const data = await getUsersAllowance();
        const arrayData = Array.isArray(data) ? data : [];

        // Only show allowances for the logged-in user's company
        const filtered = arrayData.filter(
          (item) => item.companyCode === currentUserCompanyCode
        );

        setAllowances(filtered);
      } catch (err) {
        setError("Failed to fetch document allowances.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUserCompanyCode) {
      fetchAllowances();
    }
  }, [currentUserCompanyCode]);

  // Fetch users when modal opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (!showAddModal || !currentUserCompanyCode) return;

      try {
        const users = await getAllUsers();
        const arrayUsers = Array.isArray(users) ? users : [];

        // Filter users by current company code and roleId 2 or 3
        const companyUsers = arrayUsers.filter(
          (user) =>
            user.companyCode === currentUserCompanyCode &&
            (user.roleId === 2 || user.roleId === 3)
        );
        setAvailableUsers(companyUsers);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    if (showAddModal) {
      fetchUsers();
    }
  }, [showAddModal, currentUserCompanyCode]);

  const handleAddAllowance = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await addUserAllowance({
        companyCode: currentUserCompanyCode, // ← Enforced from logged-in user
        username: formData.username.trim(),
        documentType: Number(formData.documentType),
        locationCode: formData.locationCode.trim(),
      });

      // Refresh filtered list
      const data = await getUsersAllowance();
      const filtered = (Array.isArray(data) ? data : []).filter(
        (item) => item.companyCode === currentUserCompanyCode
      );
      setAllowances(filtered);

      setShowAddModal(false);
      setFormData((prev) => ({
        ...prev,
        username: "",
        documentType: "",
        locationCode: "",
      }));
      setFormErrors({});
    } catch (err) {
      const msg =
        err.response?.data?.title ||
        err.response?.data ||
        "Failed to add allowance";
      setFormErrors({
        submit: typeof msg === "string" ? msg : "Check console",
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username.trim()) errors.username = "Required";
    if (!formData.documentType) errors.documentType = "Required";
    if (!formData.locationCode.trim()) errors.locationCode = "Required";
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Search within user's company only
  const filteredAllowances = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return allowances.filter(
      (item) =>
        (item.username || "").toLowerCase().includes(lower) ||
        (item.documentType?.toString() || "").includes(lower) ||
        (item.locationCode || "").toLowerCase().includes(lower) ||
        (docTypesMap[item.documentType] || "Unknown")
          .toLowerCase()
          .includes(lower)
    );
  }, [allowances, searchTerm, docTypesMap]);

  // Sorting
  const sortedAllowances = useMemo(() => {
    const list = [...filteredAllowances];
    const { key, direction } = sortConfig || {};
    if (!key) return list;
    list.sort((a, b) => {
      let A = a[key];
      let B = b[key];
      if (key === "documentName") {
        A = docTypesMap[a.documentType] || "Unknown";
        B = docTypesMap[b.documentType] || "Unknown";
      }
      if (typeof A === "string") A = A.toLowerCase();
      if (typeof B === "string") B = B.toLowerCase();
      if (A === B) return 0;
      const cmp = A > B ? 1 : -1;
      return direction === "asc" ? cmp : -1 * cmp;
    });
    return list;
  }, [filteredAllowances, sortConfig, docTypesMap]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
    setPageInput("1");
  }, [searchTerm, sortConfig]);

  // Keep page input synced with current page
  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev?.key === key;
      const nextDirection =
        isSameKey && prev?.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const value = parseInt(pageInput, 10);
    if (Number.isNaN(value)) return;
    const clamped = Math.min(totalPages, Math.max(1, value));
    setCurrentPage(clamped);
  };

  if (!token) return <Navigate to="/login" replace />;
  if (!username)
    return <div className="error-message">Error: Invalid session</div>;
  if (!currentUserCompanyCode) {
    return (
      <div className="completed-loading-container">Loading your profile...</div>
    );
  }

  const totalPages = Math.max(
    1,
    Math.ceil(sortedAllowances.length / itemsPerPage)
  );
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = sortedAllowances.slice(indexOfFirst, indexOfLast);

  return (
    <div className="completed-orders-container">
      <div className="completed-page-header">
        <h2 className="completed-orders-title">Document Access Permissions</h2>
        <p className="completed-page-subtitle">
          Manage document permissions for your company
        </p>
      </div>

      {error && <div className="completed-error-message">{error}</div>}

      <div className="completed-filters">
        <input
          type="text"
          placeholder="Search by username, document type, document name, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          className="completed-glass-btn completed-export-btn"
          onClick={() => setShowAddModal(true)}
        >
          <FaPlus /> Add New
        </button>
      </div>

      {/* Table without Company Code column */}
      <div className="completed-order-table-container">
        <table className="completed-order-table">
          <thead>
            <tr>
              {[
                { key: "username", label: "Username" },
                { key: "documentType", label: "Document Type" },
                { key: "documentName", label: "Document Name" },
                { key: "locationCode", label: "Location Code" },
              ].map(({ key, label }) => (
                <th
                  key={key}
                  className="completed-glass-header"
                  onClick={() => handleSort(key)}
                  style={{ cursor: "pointer" }}
                >
                  {label}
                  {sortConfig.key === key && (
                    <span className="completed-sort-icon">
                      {sortConfig.direction === "asc" ? (
                        <FaArrowUp />
                      ) : (
                        <FaArrowDown />
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, idx) => (
              <tr
                key={`${item.username}-${item.documentType}`}
                className={`completed-row-animate completed-row-animate-${Math.min(
                  idx,
                  3
                )}`}
              >
                <td>{item.username || "-"}</td>
                <td>{item.documentType ?? "-"}</td>
                <td>{docTypesMap[item.documentType] || "Unknown"}</td>
                <td>{item.locationCode || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="completed-table-footer">
          <div className="completed-row-count">
            Showing {currentItems.length} of {sortedAllowances.length}
          </div>
          <div className="completed-pagination">
            <button
              className="completed-glass-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span className="completed-pagination-info">Page</span>
            <input
              className="completed-pagination-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pageInput}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/\D/g, "");
                setPageInput(onlyNums);
              }}
              onKeyDown={handlePageInputKeyDown}
              aria-label="Current page"
            />
            <span className="completed-pagination-info">of {totalPages}</span>
            <button
              className="completed-glass-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {/* Mobile cards updated */}
        <div className="completed-order-cards">
          {currentItems.map((item, idx) => (
            <div
              key={`${item.username}-${item.documentType}`}
              className={`completed-order-card completed-row-animate completed-row-animate-${Math.min(
                idx,
                3
              )}`}
            >
              <div>
                <strong>Username:</strong> {item.username || "-"}
              </div>
              <div>
                <strong>Doc Type:</strong> {item.documentType ?? "-"}
              </div>
              <div>
                <strong>Doc Name:</strong>{" "}
                {docTypesMap[item.documentType] || "Unknown"}
              </div>
              <div>
                <strong>Location:</strong> {item.locationCode || "-"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Modal — Company Code is hidden or read-only */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Add Document Permissions</h3>
            <p className="modal-subtitle">
              Company: <strong>{currentUserCompanyCode}</strong>
            </p>

            {formErrors.submit && (
              <div className="completed-error-message">{formErrors.submit}</div>
            )}

            <div className="modal-date-inputs">
              <div className="date-field">
                <label>Username *</label>
                <select
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={formErrors.username ? "input-error" : ""}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map((user) => (
                    <option key={user.username} value={user.username}>
                      {user.username} - {user.fullName || user.name || ""}
                    </option>
                  ))}
                </select>
                {formErrors.username && (
                  <span className="field-error">{formErrors.username}</span>
                )}
              </div>

              <div className="date-field">
                <label>Document Type *</label>
                <select
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleInputChange}
                  className={formErrors.documentType ? "input-error" : ""}
                >
                  <option value="">Select a document type...</option>
                  {Object.entries(docTypesMap)
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([id, name]) => (
                      <option key={id} value={id}>
                        {id} - {name}
                      </option>
                    ))}
                </select>
                {formErrors.documentType && (
                  <span className="field-error">{formErrors.documentType}</span>
                )}
                {docTypesError && (
                  <span className="field-error">{docTypesError}</span>
                )}
              </div>

              <div className="date-field">
                <label>Location Code *</label>
                <input
                  type="text"
                  name="locationCode"
                  value={formData.locationCode}
                  onChange={handleInputChange}
                  className={formErrors.locationCode ? "input-error" : ""}
                />
                {formErrors.locationCode && (
                  <span className="field-error">{formErrors.locationCode}</span>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="completed-glass-btn cancel-btn"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                className="completed-glass-btn export-confirm-btn"
                onClick={handleAddAllowance}
              >
                Add Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAllowances;
